import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/// A seller's shop page and everything around it (ShopSetup_Spec.md; Databases/Services/ShopFront on the server). JSON is
/// PascalCase, as every API here answers. Writes answer { Done, Refused, Value }: Done with what was saved in Value, or
/// Refused saying why nothing was. The seller's reads answer the same shape ({ Done: true, Value } or { Refused }).
@Injectable({
    providedIn: 'root'
})
export class ShopFrontService {
    private readonly base = environment.apiUrl + '/api/';

    constructor(private http: HttpClient) { }

    // ---- the public page (anonymous) -------------------------------------------------------------------------------

    /// The header, About and Policies (ShopFrontView); a 404 when no shop has a page at the slug.
    shop(slug: string): Observable<any> {
        return this.http.get<any>(this.base + 'ShopFront/' + encodeURIComponent(slug || ''));
    }

    /// The Home tab (ShopHomeView): { Sections, Fallback }.
    home(slug: string): Observable<any> {
        return this.http.get<any>(this.base + 'ShopFront/' + encodeURIComponent(slug || '') + '/Home');
    }

    /// The Collections tab: ShopCollectionCard[].
    collections(slug: string): Observable<any[]> {
        return this.http.get<any[]>(this.base + 'ShopFront/' + encodeURIComponent(slug || '') + '/Collections');
    }

    /// One collection's page (ShopCollectionPage); a 404 for a collection the shop does not show.
    collection(slug: string, collection: string, page: number = 1): Observable<any> {
        const params = new HttpParams().set('page', String(page || 1));
        return this.http.get<any>(this.base + 'ShopFront/' + encodeURIComponent(slug || '') + '/Collection/' + encodeURIComponent(collection || ''), { params });
    }

    /// The Reviews tab (ShopReviewsPage).
    reviews(slug: string, page: number = 1): Observable<any> {
        const params = new HttpParams().set('page', String(page || 1));
        return this.http.get<any>(this.base + 'ShopFront/' + encodeURIComponent(slug || '') + '/Reviews', { params });
    }

    /// The categories the shop's products on sale are in (ShopCategoryChip[]: Slug, Name, Count, Parent).
    categories(slug: string): Observable<any[]> {
        return this.http.get<any[]>(this.base + 'ShopFront/' + encodeURIComponent(slug || '') + '/Categories');
    }

    /// The shops directory (ShopDirectoryPage): verified first, then by delivered orders.
    directory(q: string, cat: string, page: number = 1): Observable<any> {
        let params = new HttpParams().set('page', String(page || 1));
        if (q) { params = params.set('q', q); }
        if (cat) { params = params.set('cat', cat); }
        return this.http.get<any>(this.base + 'ShopFront/Directory', { params });
    }

    /// The home page's "Shops to follow" (ShopCard[]; empty until a shop qualifies).
    toFollow(): Observable<any[]> {
        return this.http.get<any[]>(this.base + 'ShopFront/ToFollow');
    }

    // ---- the shopper's (signed in) ----------------------------------------------------------------------------------

    /// { Following, Digest, Followers }.
    followState(slug: string): Observable<any> {
        return this.http.get<any>(this.base + 'ShopFront/' + encodeURIComponent(slug || '') + '/Follow');
    }

    /// Follows or stops following; Digest null leaves the email choice. Answers { Following, Digest, Followers } or { Refused }.
    follow(slug: string, follow: boolean, digest: boolean | null = null): Observable<any> {
        return this.http.post<any>(this.base + 'ShopFront/' + encodeURIComponent(slug || '') + '/Follow', { Follow: follow, Digest: digest });
    }

    /// Stops every followers' email to the shopper the signed link names (the /shops/email-stop page's button):
    /// { Done, Value: follows switched off } or { Refused }.
    digestStop(u: string, t: string): Observable<any> {
        const params = new HttpParams().set('u', u || '').set('t', t || '');
        return this.http.post<any>(this.base + 'ShopFront/DigestStop', {}, { params });
    }

    /// The account home's "New from shops you follow" (FollowingView): { Shops, NewProducts }.
    following(): Observable<any> {
        return this.http.get<any>(this.base + 'ShopFront/Following');
    }

    /// The shopper's conversation with this seller about their shop: { Sid, With, Thread } (Thread null until the first
    /// message), or null when the reader may not write to it. Send the first message with ConversationsService-style
    /// POST api/Messages/Send { ShopSid, Body, RequestKey }.
    shopConversation(sid: number): Observable<any> {
        return this.http.get<any>(this.base + 'Messages/Shop/' + sid);
    }

    sendToShop(sid: number, body: string, requestKey: string): Observable<any> {
        return this.http.post<any>(this.base + 'Messages/Send', { ShopSid: sid, Body: body, RequestKey: requestKey });
    }

    // ---- the seller's setup ------------------------------------------------------------------------------------------

    /// SetupChecklist: { Steps: [{ Key, Title, Hint, Done, Href }], Done, Total, Complete, CompletedAt, PreviewUrl }.
    checklist(): Observable<any> {
        return this.http.get<any>(this.base + 'ShopSetup/Checklist');
    }

    /// SectionsView: { Sections, Kinds, Limits, Collections, Bangla, PreviewUrl }.
    sections(): Observable<any> {
        return this.http.get<any>(this.base + 'ShopSetup/Sections');
    }

    /// Adds a section of a kind at the end, switched on: Value is the SectionAdminView.
    addSection(kind: string): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Sections/Add', { Kind: kind });
    }

    /// Saves one section (SectionEdit): Value is the SectionAdminView as saved.
    saveSection(model: any): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Sections/Save', model);
    }

    orderSections(ids: number[]): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Sections/Order', { Ids: ids });
    }

    deleteSection(id: number): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Sections/Delete/' + id, {});
    }

    /// What a new / bestsellers / collection section would show now: Value is PickedProduct[].
    previewSection(kind: string, days: number | null, collectionId: number | null): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Sections/Preview', { Kind: kind, Days: days, CollectionId: collectionId });
    }

    /// The seller's own products for the pickers: Value is PickedProduct[] (the newest 20 with no words).
    products(q: string): Observable<any> {
        const params = q ? new HttpParams().set('q', q) : new HttpParams();
        return this.http.get<any>(this.base + 'ShopSetup/Products', { params });
    }

    /// Value is CollectionAdminView[].
    myCollections(): Observable<any> {
        return this.http.get<any>(this.base + 'ShopSetup/Collections');
    }

    /// Saves a collection (CollectionEdit; Id 0 makes one): Value is the CollectionAdminView as saved.
    saveCollection(model: any): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Collections/Save', model);
    }

    orderCollections(ids: number[]): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Collections/Order', { Ids: ids });
    }

    deleteCollection(id: number): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Collections/Delete/' + id, {});
    }

    /// The product form's "Add to collection": Value is { Collections: [{ Id, Name, In }] }.
    collectionsOf(productId: number): Observable<any> {
        return this.http.get<any>(this.base + 'ShopSetup/Collections/Of/' + productId);
    }

    setCollectionsOf(productId: number, collectionIds: number[]): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Collections/Of/' + productId, { Ids: collectionIds });
    }

    /// PoliciesAdminView: the saved text, PlatformReturnDays, Templates { Returns, Shipping, Warranty }, Bangla.
    policies(): Observable<any> {
        return this.http.get<any>(this.base + 'ShopSetup/Policies');
    }

    savePolicies(model: any): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Policies', model);
    }

    /// { Text, TextBn, Until }.
    announcement(): Observable<any> {
        return this.http.get<any>(this.base + 'ShopSetup/Announcement');
    }

    saveAnnouncement(model: any): Observable<any> {
        return this.http.post<any>(this.base + 'ShopSetup/Announcement', model);
    }

    // ---- the owner's ---------------------------------------------------------------------------------------------------

    /// ShopPageAdminView for one seller: Value is { Sid, Name, Url, Verified, Tagline, Announcement, Checklist, Sections,
    /// Collections, Policies, Log }.
    adminPage(sid: number): Observable<any> {
        return this.http.get<any>(this.base + 'ShopPageAdmin/' + sid);
    }

    /// Switches a section off (a reason is required; the seller is shown it) or back on.
    adminSetSection(sid: number, sectionId: number, on: boolean, reason: string): Observable<any> {
        return this.http.post<any>(this.base + 'ShopPageAdmin/' + sid + (on ? '/Enable/' : '/Disable/') + sectionId, { Reason: reason });
    }

    adminVerify(sid: number, verified: boolean, reason: string): Observable<any> {
        return this.http.post<any>(this.base + 'ShopPageAdmin/' + sid + '/Verify', { Verified: verified, Reason: reason });
    }
}
